import React, { useEffect, useRef, useState } from 'react';

/*
 * Visionneuse 3D du blockout — port de tools/blockout/viewer.template.html.
 *
 * La page autonome (testée en navigateur, sans erreur console) contenait tout
 * le moteur : maths de matrices, construction du maillage depuis des boîtes,
 * shaders, orbite, marche, presets de caméra, superposition 2D. On le PORTE
 * ici sans le réinventer — seule la source de la géométrie change (elle
 * arrivait via un marqueur __GEO_JSON__ statique, elle arrive maintenant en
 * prop `layout`, tirée de `environment.layout_3d`).
 *
 * Un composant React se monte et se démonte (ce panneau est repliable). La
 * page autonome ne se démontait jamais, donc ce piège ne s'y posait pas :
 * ici, chaque montage ouvre un contexte WebGL, et les navigateurs en
 * limitent le nombre — sans nettoyage, quelques ouvertures/fermetures et la
 * page entière cesse d'afficher quoi que ce soit, sans erreur explicite.
 * D'où le useEffect avec fonction de retour ci-dessous : buffers, programme,
 * écouteurs, tout est libéré à la fermeture du panneau.
 */

const FACES = [
  { n: [0, 0, 1], v: [[-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]] },
  { n: [0, 0, -1], v: [[-1, 1, -1], [1, 1, -1], [1, -1, -1], [-1, -1, -1]] },
  { n: [1, 0, 0], v: [[1, -1, -1], [1, 1, -1], [1, 1, 1], [1, -1, 1]] },
  { n: [-1, 0, 0], v: [[-1, -1, 1], [-1, 1, 1], [-1, 1, -1], [-1, -1, -1]] },
  { n: [0, 1, 0], v: [[-1, 1, 1], [1, 1, 1], [1, 1, -1], [-1, 1, -1]] },
  { n: [0, -1, 0], v: [[-1, -1, -1], [1, -1, -1], [1, -1, 1], [-1, -1, 1]] },
];

const TINT = {
  floor: [0.055, 0.075, 0.095],
  ceiling: [0.075, 0.10, 0.125],
  wall: [0.125, 0.165, 0.20],
  furniture: [0.145, 0.255, 0.235],
};

const ROOM_FR = {
  living: 'Séjour', kitchen: 'Cuisine', core: 'Noyau technique', lab: 'Labo',
  lab_bath: 'Bain du labo', bedroom: 'Chambre', bedroom_bath: 'Bain de la chambre',
  terrace_east: 'Terrasse est', terrace_west: 'Terrasse ouest',
  terrace_north: 'Terrasse nord', terrace_south: 'Terrasse sud',
};

// On étiquette TOUTE pièce sauf celles listées ici. La liste inverse (n'étiqueter
// que 'living','kitchen','lab','bedroom') faisait disparaître toutes les étiquettes
// dès que la géométrie vient d'ailleurs : une scène Pascal nomme ses zones
// « Living / Kitchen », « Bedroom 1 »… qui ne figuraient dans aucune liste blanche.
const NOT_LABELLED = ['core', 'lab_bath', 'bedroom_bath'];
const isLabelled = (key) =>
  !key.startsWith('terrace') && !NOT_LABELLED.includes(key);

const V = {
  sub: (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]],
  add: (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]],
  cross: (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]],
  norm: (a) => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; },
};

function perspective(fovY, aspect, near, far) {
  const f = 1 / Math.tan(fovY / 2), nf = 1 / (near - far);
  return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
}

function lookAt(eye, center, up) {
  const z = V.norm(V.sub(eye, center));
  const x = V.norm(V.cross(up, z));
  const y = V.cross(z, x);
  return [
    x[0], y[0], z[0], 0,
    x[1], y[1], z[1], 0,
    x[2], y[2], z[2], 0,
    -(x[0] * eye[0] + x[1] * eye[1] + x[2] * eye[2]),
    -(y[0] * eye[0] + y[1] * eye[1] + y[2] * eye[2]),
    -(z[0] * eye[0] + z[1] * eye[1] + z[2] * eye[2]), 1];
}

function mul(a, b) {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
    let s = 0;
    for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
    o[c * 4 + r] = s;
  }
  return o;
}

function buildMesh(geo, kinds) {
  const pos = [], col = [];
  for (const b of geo.boxes) {
    if (!kinds.includes(b.kind)) continue;
    const [cx, cy, cz] = b.c, [hx, hy, hz] = b.h;
    const ca = Math.cos(b.rot), sa = Math.sin(b.rot);
    const t = TINT[b.kind];
    for (const f of FACES) {
      // normale tournée autour de Z, pour un éclairement juste sur les murs pivotés
      const nx = f.n[0] * ca - f.n[1] * sa, ny = f.n[0] * sa + f.n[1] * ca, nz = f.n[2];
      const lam = 0.42 + 0.58 * Math.max(0, nx * 0.34 + ny * -0.52 + nz * 0.78);
      const c = [t[0] * lam, t[1] * lam, t[2] * lam];
      const q = f.v.map(([sx, sy, sz]) => {
        const lx = sx * hx, ly = sy * hy;
        return [cx + lx * ca - ly * sa, cy + lx * sa + ly * ca, cz + sz * hz];
      });
      for (const i of [0, 1, 2, 0, 2, 3]) { pos.push(...q[i]); col.push(...c); }
    }
  }
  return { pos: new Float32Array(pos), col: new Float32Array(col), n: pos.length / 3 };
}

// Focale Blender -> champ vertical. Capteur 36 mm, rendu carré : fov = 2·atan(18/f).
function fovFromFocal(mm) { return 2 * Math.atan(18 / mm); }

function roomAt(geo, x, y) {
  for (const [key, r] of Object.entries(geo.rooms || {})) {
    if (x >= r[0] && x <= r[2] && y >= r[1] && y <= r[3]) return key;
  }
  return null;
}

const VS = `attribute vec3 aPos; attribute vec3 aCol; uniform mat4 uMVP;
varying vec3 vCol; varying float vDepth;
void main(){ vec4 p = uMVP * vec4(aPos,1.0); gl_Position = p; vCol = aCol; vDepth = p.z/p.w; }`;

const FS = `precision mediump float; varying vec3 vCol; varying float vDepth;
void main(){ gl_FragColor = vec4(vCol, 1.0); }`;

function compileShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src); gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error(info);
  }
  return s;
}

// Clé stable de la géométrie : `current.layout_3d` est un nouvel objet à
// chaque refetch de l'environnement (upload, génération, changement de rôle…)
// même quand son contenu n'a pas changé. Sans cette clé, le composant
// recréerait un contexte WebGL — et perdrait la caméra de l'utilisateur — à
// chaque action sans rapport dans le panneau.
function geoSignature(layout) {
  if (!layout || !layout.boxes || !layout.boxes.length) return null;
  return `${layout.published_at || layout.rendered_at || ''}:${layout.boxes.length}`;
}

const BlockoutViewer = ({ layout }) => {
  const stageRef = useRef(null);
  const glCanvasRef = useRef(null);
  const ovCanvasRef = useRef(null);
  const engineRef = useRef(null);

  const [glAvailable, setGlAvailable] = useState(true);
  const [mode, setMode] = useState('orbit');
  const [activeCam, setActiveCam] = useState(null);
  const [showCeiling, setShowCeiling] = useState(false);
  const [showFurniture, setShowFurniture] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showFrame, setShowFrame] = useState(true);
  const [readout, setReadout] = useState({ pos: '—', room: '—', mm: '—', cam: 'libre' });

  const sig = geoSignature(layout);

  // Les toggles d'affichage sont lus par la boucle de rendu via cette ref :
  // ça évite de reconstruire le moteur (et le contexte WebGL) à chaque clic
  // sur une case.
  const displayRef = useRef({ ceiling: showCeiling, furn: showFurniture, labels: showLabels, frame: showFrame });
  useEffect(() => {
    displayRef.current = { ceiling: showCeiling, furn: showFurniture, labels: showLabels, frame: showFrame };
    engineRef.current?.frame();
  }, [showCeiling, showFurniture, showLabels, showFrame]);

  useEffect(() => {
    if (!sig) return undefined;
    const glc = glCanvasRef.current, ovc = ovCanvasRef.current, stage = stageRef.current;
    if (!glc || !ovc || !stage) return undefined;

    const gl = glc.getContext('webgl', { antialias: true });
    if (!gl) { setGlAvailable(false); return undefined; }
    setGlAvailable(true);

    let prog, aPos, aCol, uMVP;
    let groups = {};
    let disposed = false;

    try {
      prog = gl.createProgram();
      const vs = compileShader(gl, gl.VERTEX_SHADER, VS);
      const fs = compileShader(gl, gl.FRAGMENT_SHADER, FS);
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      gl.useProgram(prog);
      aPos = gl.getAttribLocation(prog, 'aPos');
      aCol = gl.getAttribLocation(prog, 'aCol');
      uMVP = gl.getUniformLocation(prog, 'uMVP');
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.clearColor(0.027, 0.035, 0.047, 1);

      for (const [key, kinds] of Object.entries({
        structure: ['floor', 'wall'], ceiling: ['ceiling'], furniture: ['furniture'],
      })) {
        const m = buildMesh(layout, kinds);
        const pb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, pb);
        gl.bufferData(gl.ARRAY_BUFFER, m.pos, gl.STATIC_DRAW);
        const cb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, cb);
        gl.bufferData(gl.ARRAY_BUFFER, m.col, gl.STATIC_DRAW);
        groups[key] = { pb, cb, n: m.n };
      }
    } catch (err) {
      // Un shader qui refuse de compiler n'est pas un plateau vide : dis-le.
      setGlAvailable(false);
      return undefined;
    }

    const [EX0, EY0, EX1, EY1] = layout.envelope || [0, 0, 10, 10];
    const MID = [(EX0 + EX1) / 2, (EY0 + EY1) / 2, 1.4];

    const cam = {
      mode: 'orbit',
      target: MID.slice(),
      dist: 19,
      yaw: -1.05,
      pitch: 0.42,
      eye: [4.7, 0.95, 1.55],
      look: [-0.55, 0.25],
      focal: null,
      code: null,
    };

    function orbitEye() {
      const cp = Math.cos(cam.pitch);
      return [
        cam.target[0] + cam.dist * cp * Math.cos(cam.yaw),
        cam.target[1] + cam.dist * cp * Math.sin(cam.yaw),
        cam.target[2] + cam.dist * Math.sin(cam.pitch),
      ];
    }

    function walkDir() {
      const cp = Math.cos(cam.look[1]);
      return [cp * Math.cos(cam.look[0]), cp * Math.sin(cam.look[0]), Math.sin(cam.look[1])];
    }

    function currentEyeCenter() {
      if (cam.mode === 'orbit') return [orbitEye(), cam.target];
      return [cam.eye, V.add(cam.eye, walkDir())];
    }

    let W = 1, H = 1, DPR = 1, mvp = null;

    function resize() {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      W = stage.clientWidth || 1; H = stage.clientHeight || 1;
      for (const c of [glc, ovc]) { c.width = Math.round(W * DPR); c.height = Math.round(H * DPR); }
      gl.viewport(0, 0, glc.width, glc.height);
    }

    function drawGroup(g) {
      gl.bindBuffer(gl.ARRAY_BUFFER, g.pb);
      gl.enableVertexAttribArray(aPos); gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, g.cb);
      gl.enableVertexAttribArray(aCol); gl.vertexAttribPointer(aCol, 3, gl.FLOAT, false, 0, 0);
      gl.drawArrays(gl.TRIANGLES, 0, g.n);
    }

    function project(p) {
      if (!mvp) return null;
      const x = mvp[0] * p[0] + mvp[4] * p[1] + mvp[8] * p[2] + mvp[12];
      const y = mvp[1] * p[0] + mvp[5] * p[1] + mvp[9] * p[2] + mvp[13];
      const w = mvp[3] * p[0] + mvp[7] * p[1] + mvp[11] * p[2] + mvp[15];
      if (w <= 0.02) return null;
      return [(x / w * 0.5 + 0.5) * W, (1 - (y / w * 0.5 + 0.5)) * H];
    }

    const ov = ovc.getContext('2d');

    function overlay(fovY) {
      ov.setTransform(DPR, 0, 0, DPR, 0, 0);
      ov.clearRect(0, 0, W, H);
      const disp = displayRef.current;

      // Cadre du rendu 1:1 : ce que la carte de profondeur cadre réellement.
      if (disp.frame && cam.focal) {
        const side = Math.min(W, H) * 0.94;
        const x = (W - side) / 2, y = (H - side) / 2;
        ov.strokeStyle = 'rgba(99,179,164,0.55)';
        ov.lineWidth = 1;
        ov.setLineDash([5, 4]);
        ov.strokeRect(x, y, side, side);
        ov.setLineDash([]);
        ov.fillStyle = 'rgba(99,179,164,0.8)';
        ov.font = '10px ui-monospace, Menlo, monospace';
        ov.fillText('cadre 1:1', x + 6, y - 6);
      }

      // Noms des pièces, posés au sol.
      if (disp.labels) {
        ov.font = '11px ui-monospace, Menlo, monospace';
        ov.textAlign = 'center';
        for (const [key, r] of Object.entries(layout.rooms || {})) {
          if (!isLabelled(key)) continue;
          const p = project([(r[0] + r[2]) / 2, (r[1] + r[3]) / 2, 0.05]);
          if (!p) continue;
          ov.fillStyle = 'rgba(154,167,178,0.62)';
          ov.fillText((ROOM_FR[key] || key).toUpperCase(), p[0], p[1]);
        }
        ov.textAlign = 'left';
      }

      // Repères des caméras canon.
      ov.font = '10px ui-monospace, Menlo, monospace';
      for (const [code, c] of Object.entries(layout.cameras || {})) {
        if (cam.mode === 'walk' && cam.code === code) continue;
        const p = project(c.pos);
        if (!p) continue;
        const on = cam.code === code;
        ov.fillStyle = on ? 'rgba(143,212,198,0.95)' : 'rgba(99,179,164,0.5)';
        ov.beginPath();
        ov.moveTo(p[0], p[1] - 4); ov.lineTo(p[0] + 4, p[1]);
        ov.lineTo(p[0], p[1] + 4); ov.lineTo(p[0] - 4, p[1]);
        ov.closePath(); ov.fill();
        if (on || cam.mode === 'orbit') ov.fillText(code, p[0] + 7, p[1] + 3);
      }
    }

    function readoutTick(eye) {
      const rk = roomAt(layout, eye[0], eye[1]);
      setReadout({
        pos: eye.map((v) => v.toFixed(1)).join(' / ') + ' m',
        room: rk ? (ROOM_FR[rk] || rk) : 'hors plan',
        mm: cam.focal ? cam.focal + ' mm' : '—',
        cam: cam.code || 'libre',
      });
    }

    function frame() {
      if (disposed) return;
      const [eye, center] = currentEyeCenter();
      const aspect = W / H;
      const fovY = cam.focal ? fovFromFocal(cam.focal) : (55 * Math.PI / 180);
      const proj = perspective(fovY, aspect, 0.08, 90);
      mvp = mul(proj, lookAt(eye, center, [0, 0, 1]));

      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.uniformMatrix4fv(uMVP, false, mvp);
      drawGroup(groups.structure);
      if (displayRef.current.ceiling) drawGroup(groups.ceiling);
      if (displayRef.current.furn) drawGroup(groups.furniture);

      overlay(fovY);
      readoutTick(eye);
    }

    function step(m) {
      const d = walkDir();
      cam.eye = [cam.eye[0] + d[0] * m, cam.eye[1] + d[1] * m, cam.eye[2]];
      cam.code = null; setActiveCam(null);
    }

    function strafe(m) {
      const d = walkDir();
      const s = V.norm(V.cross([0, 0, 1], [d[0], d[1], 0]));
      cam.eye = [cam.eye[0] + s[0] * m, cam.eye[1] + s[1] * m, cam.eye[2]];
      cam.code = null; setActiveCam(null);
    }

    let drag = null;
    function onPointerDown(e) { glc.setPointerCapture(e.pointerId); drag = { x: e.clientX, y: e.clientY }; }
    function onPointerMove(e) {
      if (!drag) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      drag.x = e.clientX; drag.y = e.clientY;
      if (cam.mode === 'orbit') {
        cam.yaw -= dx * 0.006;
        cam.pitch = Math.max(-0.25, Math.min(1.45, cam.pitch + dy * 0.005));
      } else {
        cam.look[0] -= dx * 0.005;
        cam.look[1] = Math.max(-1.2, Math.min(1.2, cam.look[1] - dy * 0.004));
        // on garde la focale de la vue quittée : on inspecte le même objectif
        cam.code = null; setActiveCam(null);
      }
      frame();
    }
    function onPointerUp() { drag = null; }
    function onWheel(e) {
      e.preventDefault();
      if (cam.mode === 'orbit') {
        cam.dist = Math.max(3, Math.min(70, cam.dist * (1 + Math.sign(e.deltaY) * 0.09)));
      } else {
        step(-Math.sign(e.deltaY) * 0.5);
      }
      frame();
    }

    let pinch = null;
    function onTouchStart(e) {
      if (e.touches.length === 2) {
        pinch = Math.hypot(e.touches[0].clientX - e.touches[1].clientX,
                           e.touches[0].clientY - e.touches[1].clientY);
      }
    }
    function onTouchMove(e) {
      if (e.touches.length === 2 && pinch) {
        const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX,
                             e.touches[0].clientY - e.touches[1].clientY);
        if (cam.mode === 'orbit') cam.dist = Math.max(3, Math.min(70, cam.dist * pinch / d));
        pinch = d; frame();
      }
    }
    function onTouchEnd() { pinch = null; }

    function onKeyDown(e) {
      if (cam.mode !== 'walk') return;
      const k = e.key.toLowerCase();
      const m = e.shiftKey ? 0.8 : 0.35;
      if (k === 'w' || k === 'z' || e.key === 'ArrowUp') step(m);
      else if (k === 's' || e.key === 'ArrowDown') step(-m);
      else if (k === 'a' || k === 'q' || e.key === 'ArrowLeft') strafe(-m);
      else if (k === 'd' || e.key === 'ArrowRight') strafe(m);
      else return;
      e.preventDefault(); frame();
    }

    function gotoCam(code) {
      const c = (layout.cameras || {})[code];
      if (!c) return;
      cam.code = code;
      cam.focal = c.focal_mm;
      setModeInternal('walk', true);
      cam.eye = c.pos.slice();
      const d = V.norm(V.sub(c.target, c.pos));
      cam.look = [Math.atan2(d[1], d[0]), Math.asin(Math.max(-1, Math.min(1, d[2])))];
      setActiveCam(code);
      frame();
    }

    function setModeInternal(m, keep) {
      cam.mode = m;
      setMode(m);
      if (!keep) { cam.code = null; cam.focal = null; setActiveCam(null); }
      frame();
    }

    const ro = ('ResizeObserver' in window) ? new ResizeObserver(() => { resize(); frame(); }) : null;
    if (ro) ro.observe(stage);
    else window.addEventListener('resize', () => { resize(); frame(); });

    glc.addEventListener('pointerdown', onPointerDown);
    glc.addEventListener('pointermove', onPointerMove);
    glc.addEventListener('pointerup', onPointerUp);
    glc.addEventListener('pointercancel', onPointerUp);
    glc.addEventListener('wheel', onWheel, { passive: false });
    glc.addEventListener('touchstart', onTouchStart, { passive: true });
    glc.addEventListener('touchmove', onTouchMove, { passive: true });
    glc.addEventListener('touchend', onTouchEnd);
    window.addEventListener('keydown', onKeyDown);

    engineRef.current = { setMode: setModeInternal, gotoCam, frame, step, strafe };

    resize();
    frame();

    return () => {
      disposed = true;
      engineRef.current = null;
      if (ro) ro.disconnect();
      glc.removeEventListener('pointerdown', onPointerDown);
      glc.removeEventListener('pointermove', onPointerMove);
      glc.removeEventListener('pointerup', onPointerUp);
      glc.removeEventListener('pointercancel', onPointerUp);
      glc.removeEventListener('wheel', onWheel);
      glc.removeEventListener('touchstart', onTouchStart);
      glc.removeEventListener('touchmove', onTouchMove);
      glc.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKeyDown);
      for (const grp of Object.values(groups)) {
        gl.deleteBuffer(grp.pb);
        gl.deleteBuffer(grp.cb);
      }
      gl.deleteProgram(prog);
      // Perte de contexte explicite : sur certains navigateurs, un contexte
      // WebGL abandonné sans deleteBuffer/deleteProgram ni loseContext reste
      // compté dans la limite jusqu'au passage du GC — imprévisible au bout
      // de quelques ouvertures/fermetures du panneau.
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    };
    // `layout` (la géométrie) ne change qu'à une nouvelle publication : `sig`
    // le capture sans se déclencher sur les refetch qui ne la touchent pas.
  }, [sig]);

  if (!layout || !layout.boxes || !layout.boxes.length) {
    return (
      <div className="blk-empty" data-testid="blk-empty">
        Blockout non publié pour ce lieu. Depuis le poste de rendu :
        <code>python3 tools/blockout/publish.py --dir &lt;sortie&gt;</code>
      </div>
    );
  }

  const cameras = Object.entries(layout.cameras || {});

  return (
    <div className="blk-wrap" data-testid="blk-viewer">
      <div className="blk-rail">
        <div className="blk-group">
          <div className="blk-group-title">Vues canon</div>
          <div className="blk-cams">
            {cameras.map(([code, c]) => (
              <button key={code} type="button" className="blk-cam"
                aria-pressed={activeCam === code}
                onClick={() => engineRef.current?.gotoCam(code)}>
                <span>{code}</span><span className="blk-mm">{c.focal_mm} mm</span>
              </button>
            ))}
            {cameras.length === 0 && <p className="form-hint">Aucune vue canon.</p>}
          </div>
        </div>

        <div className="blk-group">
          <div className="blk-group-title">Navigation</div>
          <div className="blk-modes">
            <button type="button" className="blk-mode" aria-pressed={mode === 'orbit'}
              onClick={() => engineRef.current?.setMode('orbit')}>Orbite</button>
            <button type="button" className="blk-mode" aria-pressed={mode === 'walk'}
              onClick={() => engineRef.current?.setMode('walk')}>Marche</button>
          </div>
          <p className="form-hint" style={{ marginTop: 9 }}>
            {mode === 'orbit'
              ? 'Glisser pour tourner, molette ou pincement pour approcher. Une vue canon passe en marche, à la position et la focale exactes du rendu.'
              : 'Glisser pour regarder autour. ZQSD ou les flèches pour se déplacer, Maj pour aller plus vite.'}
          </p>
        </div>

        <div className="blk-group">
          <div className="blk-group-title">Affichage</div>
          <div className="blk-toggles">
            <label className="blk-tg"><input type="checkbox" checked={showCeiling}
              onChange={(e) => setShowCeiling(e.target.checked)} /> Plafond</label>
            <label className="blk-tg"><input type="checkbox" checked={showFurniture}
              onChange={(e) => setShowFurniture(e.target.checked)} /> Mobilier</label>
            <label className="blk-tg"><input type="checkbox" checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)} /> Noms des pièces</label>
            <label className="blk-tg"><input type="checkbox" checked={showFrame}
              onChange={(e) => setShowFrame(e.target.checked)} /> Cadre du rendu 1:1</label>
          </div>
        </div>

        <div className="blk-group blk-group-last">
          <div className="blk-group-title">Relevé</div>
          <dl className="blk-readout">
            <dt>Vue</dt><dd>{readout.cam}</dd>
            <dt>Œil</dt><dd>{readout.pos}</dd>
            <dt>Pièce</dt><dd className="blk-room">{readout.room}</dd>
            <dt>Focale</dt><dd>{readout.mm}</dd>
          </dl>
        </div>
      </div>

      <div className="blk-stage" ref={stageRef}>
        <canvas ref={glCanvasRef} className={`blk-gl${mode === 'walk' ? ' walking' : ''}`} />
        <canvas ref={ovCanvasRef} className="blk-ov" />
        {!glAvailable && (
          <p className="blk-error">WebGL indisponible dans ce navigateur : la vue 3D ne peut pas s'afficher.</p>
        )}
      </div>
    </div>
  );
};

export default BlockoutViewer;
